import { useElementSize } from '@/common/hooks/useElementSize'
import CirclesGraph from '@/graph/CirclesGraph'
import {
  parseOrgGraphView,
  useDisabledGraphViews,
} from '@/graph/instanceGraphViews'
import { CirclesGraphViews } from '@/graph/types'
import useGraphEvents from '@/graph/hooks/useGraphEvents'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { useNavigateOrg } from '@/org/hooks/useNavigateOrg'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  BoxProps,
  Button,
  useColorMode,
} from '@chakra-ui/react'
import { getOrgPath } from '@rolebase/shared/helpers/getOrgPath'
import { defaultGraphView } from '@rolebase/shared/model/graph'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useOpenOrgChartOnZoom } from '../hooks/useOpenOrgChartOnZoom'

export default function DashboardOrgChart(boxProps: BoxProps) {
  const { t } = useTranslation()
  const navigateOrg = useNavigateOrg()

  // Content size
  const boxRef = useRef<HTMLDivElement>(null)
  const boxSize = useElementSize(boxRef)
  const size = boxSize?.width // Square

  // Data
  const { org, orgData } = useOrgContext()
  const disabledViews = useDisabledGraphViews()
  const circles = orgData?.circles
  const { onCircleClick, onMemberClick } = useGraphEvents()
  const events = useMemo(() => ({ onCircleClick, onMemberClick }), [])

  // The organization default view, unfolded: the whole org at a glance
  // (Yohan, 24/09/2026, instead of Godefroy's folded preview). The links view
  // draws its own web outside the engine, and the whiteboard (#200) is no
  // graph: the preview shows the circles there.
  const parsed =
    parseOrgGraphView(
      disabledViews,
      org?.defaultGraphView,
      undefined,
      org?.defaultGraphMembers
    ) || defaultGraphView
  const view =
    parsed.view === CirclesGraphViews.RoleLinks ||
    parsed.view === CirclesGraphViews.Whiteboard
      ? CirclesGraphViews.Circles
      : parsed.view
  const { members } = parsed

  // Color mode
  const { colorMode } = useColorMode()

  // A zoom gesture (wheel/pinch) opens the full, interactive org chart.
  useOpenOrgChartOnZoom(boxRef)

  // Redirect to org chart when there is only one circle
  useEffect(() => {
    if (circles?.length === 1 && org) {
      navigateOrg(`roles`)
    }
  }, [circles, org])

  if (circles?.length === 1 && org) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>{t('DashboardOrgChart.empty')}</AlertDescription>
        <Link to={`${getOrgPath(org)}/roles`}>
          <Button colorScheme="blue">{t('DashboardOrgChart.edit')}</Button>
        </Link>
      </Alert>
    )
  }

  return (
    <Box ref={boxRef} h={size} {...boxProps}>
      {org && orgData && size && (
        <CirclesGraph
          key={`${view}${members}${colorMode}`}
          view={view}
          hideMembers={!members}
          org={orgData}
          events={events}
          width={size}
          height={size}
          panzoomDisabled
          minimap={false}
        />
      )}
    </Box>
  )
}
